import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Upload, GripVertical, AlertCircle, User } from 'lucide-react';

import { API_URL } from '../../config/api';

const MAX_DOCTORS = 8;
const CHAR_LIMITS = {
    name: 60,
    specialty: 80,
    bio: 300
};

// Helper to resolve image URLs - relative paths need API_URL prefix
const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${API_URL}${url}`;
};

const DoctorManager = () => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [meta, setMeta] = useState({ active: 0, can_add_more: true });
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/site-team/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setDoctors(data.data || []);
                setMeta(data.meta || { active: 0, can_add_more: true });
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
            setMessage({ type: 'error', text: 'Error al cargar doctores' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (doctor) => {
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const token = localStorage.getItem('token');
            const isNew = !doctor.id;
            const url = isNew ? `${API_URL}/site-team` : `${API_URL}/site-team/${doctor.id}`;
            const method = isNew ? 'POST' : 'PUT';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(doctor)
            });

            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: isNew ? 'Doctor agregado' : 'Doctor actualizado' });
                fetchDoctors();
                setShowForm(false);
                setEditingDoctor(null);
            } else {
                setMessage({ type: 'error', text: data.message || 'Error al guardar' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error de conexión' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este doctor?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/site-team/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Doctor eliminado' });
                fetchDoctors();
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al eliminar' });
        }
    };

    const handleToggleActive = async (doctor) => {
        await handleSave({ ...doctor, is_active: !doctor.is_active });
    };

    const handleImageUpload = async (e, doctor, setDoctor) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'doctors');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                setDoctor({ ...doctor, photo_url: data.data.url });
            } else {
                setMessage({ type: 'error', text: data.message || 'Error al subir imagen' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al subir imagen' });
        }
    };

    const CharCounter = ({ value, limit }) => {
        const count = (value || '').length;
        const percentage = (count / limit) * 100;
        return (
            <span className={`char-counter ${percentage > 90 ? 'warning' : ''} ${percentage >= 100 ? 'error' : ''}`}>
                {count}/{limit}
            </span>
        );
    };

    const DoctorForm = ({ doctor, onSave, onCancel }) => {
        const [formData, setFormData] = useState(doctor || {
            name: '',
            specialty: '',
            bio: '',
            photo_url: '',
            is_active: true
        });

        return (
            <div className="doctor-form">
                <div className="form-row">
                    <div className="form-group photo-upload">
                        <div className="photo-preview">
                            {formData.photo_url ? (
                                <img src={getImageUrl(formData.photo_url)} alt="Preview" />
                            ) : (
                                <User size={48} />
                            )}
                        </div>
                        <label className="upload-btn">
                            <Upload size={16} />
                            Subir Foto
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, formData, setFormData)}
                                hidden
                            />
                        </label>
                        <span className="photo-hint">Cualquier tamaño (se ajusta auto) - Máx 25MB</span>
                    </div>

                    <div className="form-fields">
                        <div className="form-group">
                            <label>
                                Nombre
                                <CharCounter value={formData.name} limit={CHAR_LIMITS.name} />
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, CHAR_LIMITS.name) })}
                                placeholder="Dr. Juan García"
                                maxLength={CHAR_LIMITS.name}
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Especialidad
                                <CharCounter value={formData.specialty} limit={CHAR_LIMITS.specialty} />
                            </label>
                            <input
                                type="text"
                                value={formData.specialty}
                                onChange={(e) => setFormData({ ...formData, specialty: e.target.value.slice(0, CHAR_LIMITS.specialty) })}
                                placeholder="Médico Genetista Certificado"
                                maxLength={CHAR_LIMITS.specialty}
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Biografía
                                <CharCounter value={formData.bio} limit={CHAR_LIMITS.bio} />
                            </label>
                            <textarea
                                value={formData.bio}
                                onChange={(e) => setFormData({ ...formData, bio: e.target.value.slice(0, CHAR_LIMITS.bio) })}
                                placeholder="Breve descripción profesional..."
                                rows={3}
                                maxLength={CHAR_LIMITS.bio}
                            />
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                Visible en el sitio
                            </label>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button className="btn btn-secondary" onClick={onCancel}>
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => onSave(formData)}
                        disabled={saving || !formData.name || !formData.specialty}
                    >
                        <Save size={16} />
                        {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="loading-state">Cargando equipo médico...</div>;
    }

    return (
        <div className="doctor-manager">
            <div className="manager-header">
                <div className="header-info">
                    <h3>Equipo Médico</h3>
                    <span className="count-badge">
                        {meta.active} / {MAX_DOCTORS} activos
                    </span>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => { setEditingDoctor(null); setShowForm(true); }}
                    disabled={!meta.can_add_more}
                >
                    <Plus size={16} />
                    Agregar Doctor
                </button>
            </div>

            {!meta.can_add_more && (
                <div className="limit-warning">
                    <AlertCircle size={16} />
                    Límite alcanzado. Máximo {MAX_DOCTORS} doctores activos para mantener el diseño.
                </div>
            )}

            {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
            )}

            {showForm && (
                <DoctorForm
                    doctor={editingDoctor}
                    onSave={handleSave}
                    onCancel={() => { setShowForm(false); setEditingDoctor(null); }}
                />
            )}

            <div className="doctors-list">
                {doctors.map((doctor) => (
                    <div key={doctor.id} className={`doctor-card ${!doctor.is_active ? 'inactive' : ''}`}>
                        <div className="drag-handle">
                            <GripVertical size={16} />
                        </div>
                        <div className="doctor-photo">
                            {doctor.photo_url ? (
                                <img src={getImageUrl(doctor.photo_url)} alt={doctor.name} />
                            ) : (
                                <User size={32} />
                            )}
                        </div>
                        <div className="doctor-info">
                            <h4>{doctor.name}</h4>
                            <span className="specialty">{doctor.specialty}</span>
                            {!doctor.is_active && <span className="inactive-badge">Oculto</span>}
                        </div>
                        <div className="doctor-actions">
                            <button
                                className="btn-icon"
                                onClick={() => { setEditingDoctor(doctor); setShowForm(true); }}
                                title="Editar"
                            >
                                ✏️
                            </button>
                            <button
                                className="btn-icon"
                                onClick={() => handleToggleActive(doctor)}
                                title={doctor.is_active ? 'Ocultar' : 'Mostrar'}
                            >
                                {doctor.is_active ? '👁️' : '👁️‍🗨️'}
                            </button>
                            <button
                                className="btn-icon danger"
                                onClick={() => handleDelete(doctor.id)}
                                title="Eliminar"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}

                {doctors.length === 0 && (
                    <div className="empty-state">
                        No hay doctores registrados. Agrega el primero.
                    </div>
                )}
            </div>
        </div>
    );
};

export default DoctorManager;
